import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  useDeletePurchaseOrder,
  useOrderingStatus,
  usePurchaseOrder,
  useSendPurchaseOrder,
  useUpdatePurchaseOrder,
} from '../hooks/use-purchase-orders'
import { usePurchaseOrderRestaurantStore } from '../store/purchase-order-restaurant-store'
import {
  afterCutoffOf,
  purchaseOrderErrorMessage,
} from '../lib/purchase-order-errors'
import { editLinesFromOrder } from '../lib/purchase-order-lines'
import { PurchaseOrderStatusBadges } from '../components/purchase-order-columns'
import { PurchaseOrderDraftEditor } from '../components/purchase-order-draft-editor'
import { OrderingNotConfigured } from '../components/ordering-not-configured'
import {
  LINES_CONTAINER,
  PurchaseOrderLineRow,
  PurchaseOrderLinesHeader,
} from '../components/purchase-order-line-row'
import type { AfterCutoff } from '../lib/purchase-order-errors'
import type {
  PurchaseOrder,
  PurchaseOrderUpdateBody,
} from '@/api/endpoints/purchase-orders'
import {
  formatIsoDate,
  formatTimestamp,
} from '@/features/inventory-deliveries/components/delivery-columns'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

function Fact({
  label,
  children,
  mono = false,
}: {
  label: string
  children: React.ReactNode
  mono?: boolean
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={
          mono
            ? 'font-mono text-sm break-all'
            : 'text-sm font-medium break-words'
        }
      >
        {children}
      </p>
    </div>
  )
}

/** A sent, failed, unclear or delivered order — and a draft for someone who may only look. */
function ReadOnlyOrder({ order }: { order: PurchaseOrder }) {
  const { t } = useTranslation('purchase-orders')
  const lines = editLinesFromOrder(order)
  const sent = order.sentAt != null || order.orderNumber != null
  return (
    <>
      <div className="grid grid-cols-2 gap-x-8 gap-y-3 rounded-lg border bg-muted/30 px-4 py-3 sm:grid-cols-4">
        <Fact label={t('detail.orderNumber')} mono>
          {order.orderNumber ?? '–'}
        </Fact>
        <Fact label={t('detail.supplier')}>{order.supplierName ?? '–'}</Fact>
        <Fact label={t('detail.deliveryDate')}>
          {formatIsoDate(order.deliveryDate)}
        </Fact>
        <Fact label={t('detail.countsForPreorders')}>
          {order.countsForPreorders ? t('detail.yes') : t('detail.no')}
        </Fact>
        {sent && (
          <>
            <Fact label={t('detail.sentBy')}>{order.sentBy ?? '–'}</Fact>
            <Fact label={t('detail.sentAt')}>
              {formatTimestamp(order.sentAt)}
            </Fact>
            <Fact label={t('detail.ediRecipient')} mono>
              {order.ediRecipient ?? '–'}
            </Fact>
            <Fact label={t('detail.copyRecipients')} mono>
              {order.copyRecipients && order.copyRecipients.length > 0
                ? order.copyRecipients.join(', ')
                : '–'}
            </Fact>
          </>
        )}
        {order.note && (
          <div className="col-span-2 sm:col-span-4">
            <Fact label={t('detail.note')}>{order.note}</Fact>
          </div>
        )}
      </div>

      {order.deliveryId && (
        <div>
          <Button variant="link" className="px-0" asChild>
            <Link
              to="/inventory/deliveries/$id"
              params={{ id: order.deliveryId }}
              search={{ restaurantId: order.restaurantId }}
            >
              {`${t('detail.openDelivery')} →`}
            </Link>
          </Button>
        </div>
      )}

      <Card className="overflow-hidden py-0">
        <CardContent className="p-0">
          <div className={LINES_CONTAINER}>
            <PurchaseOrderLinesHeader readOnly />
            {lines.map((line) => (
              <PurchaseOrderLineRow key={line.key} line={line} readOnly />
            ))}
          </div>
          {lines.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t('detail.noLines')}
            </p>
          )}
        </CardContent>
      </Card>
    </>
  )
}

/** Lager → Bestellungen → one order (design UI, tasks 6.4 and 6.5). */
export function PurchaseOrderDetailPage() {
  return (
    <PermissionGate action={ACTIONS.PURCHASE_ORDER_VIEW}>
      <PurchaseOrderDetailContent />
    </PermissionGate>
  )
}

/** Behind the gate, so nothing is fetched without the permission. */
function PurchaseOrderDetailContent() {
  const { t } = useTranslation('purchase-orders')
  const navigate = useNavigate()
  const { id } = useParams({ strict: false })
  const { restaurantId: linkedRestaurantId } = useSearch({ strict: false })
  const restaurantId = usePurchaseOrderRestaurantStore((s) => s.restaurantId)
  const setRestaurantId = usePurchaseOrderRestaurantStore(
    (s) => s.setRestaurantId,
  )
  // A link from the Bestellvorschlag names its Standort; adopt it for the fetch and the list.
  useEffect(() => {
    if (linkedRestaurantId != null && linkedRestaurantId !== restaurantId) {
      setRestaurantId(linkedRestaurantId)
    }
  }, [linkedRestaurantId, restaurantId, setRestaurantId])
  const effectiveRestaurantId = linkedRestaurantId ?? restaurantId

  const { can } = usePermissions()
  const canOrder = can(ACTIONS.PURCHASE_ORDER_ORDER)
  const {
    data: order,
    isLoading,
    isError,
  } = usePurchaseOrder(effectiveRestaurantId, id ?? null)
  const ordering = useOrderingStatus(effectiveRestaurantId)
  const update = useUpdatePurchaseOrder(effectiveRestaurantId, id ?? null)
  const send = useSendPurchaseOrder(effectiveRestaurantId, id ?? null)
  const remove = useDeletePurchaseOrder(effectiveRestaurantId, id ?? null)

  const [cutoff, setCutoff] = useState<AfterCutoff | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const busy = update.isPending || send.isPending || remove.isPending
  const configured = ordering.data?.configured === true

  const fail = (error: unknown) =>
    toast.error(purchaseOrderErrorMessage(error, t))

  const save = (body: PurchaseOrderUpdateBody) =>
    update.mutateAsync(body).then(
      () => toast.success(t('toast.saved')),
      (error: unknown) => {
        fail(error)
        throw error
      },
    )

  const sendOrder = (confirmAfterCutoff: boolean) =>
    send.mutate(confirmAfterCutoff, {
      onSuccess: (sent) =>
        toast.success(t('toast.sent', { number: sent.orderNumber ?? '' })),
      onError: (error) => {
        const late = afterCutoffOf(error)
        if (late) setCutoff(late)
        else fail(error)
      },
    })

  const saveAndSend = (body: PurchaseOrderUpdateBody | null) => {
    if (!body) {
      sendOrder(false)
      return
    }
    update.mutateAsync(body).then(
      () => sendOrder(false),
      (error: unknown) => fail(error),
    )
  }

  const back = (
    <Button variant="outline" asChild>
      <Link to="/inventory/purchase-orders">{t('detail.back')}</Link>
    </Button>
  )

  const editable = order?.status === 'DRAFT' && canOrder
  const resendable =
    canOrder &&
    order != null &&
    (order.status === 'FAILED' ||
      (order.status === 'SENDING' && order.statusUnclear))

  return (
    <div className="flex flex-col gap-4">
      {isLoading && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-8 w-64" />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {!isLoading && (isError || !order) && (
        <div className="flex flex-col items-start gap-3 py-8">
          <p className="text-sm text-muted-foreground">
            {t('detail.notFound')}
          </p>
          {back}
        </div>
      )}

      {order && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold">
                {order.orderNumber
                  ? t('detail.titleNumber', { number: order.orderNumber })
                  : t('detail.title')}
              </h1>
              <PurchaseOrderStatusBadges order={order} t={t} />
            </div>
            <div className="flex items-center gap-2">
              {resendable && (
                <Button onClick={() => sendOrder(false)} disabled={busy}>
                  {t('detail.resend')}
                </Button>
              )}
              {back}
            </div>
          </div>

          {order.status === 'FAILED' && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t('detail.failed')}</AlertTitle>
              <AlertDescription>{order.failureReason ?? '–'}</AlertDescription>
            </Alert>
          )}
          {order.status === 'SENDING' && order.statusUnclear && (
            <Alert className="border-warning/50 bg-warning/10">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t('status.unclear')}</AlertTitle>
              <AlertDescription>{t('detail.unclearHint')}</AlertDescription>
            </Alert>
          )}
          {order.status === 'SENDING' && !order.statusUnclear && (
            <p className="text-sm text-muted-foreground">
              {t('detail.sending')}
            </p>
          )}

          {(editable || resendable) &&
            ordering.data &&
            !ordering.data.configured && (
              <OrderingNotConfigured
                restaurantId={order.restaurantId}
                missing={ordering.data.missing}
              />
            )}

          {editable ? (
            <PurchaseOrderDraftEditor
              key={`${order.id}-${order.updatedAt ?? ''}`}
              order={order}
              canSend={configured}
              busy={busy}
              onSave={save}
              onSend={saveAndSend}
              onDelete={() => setDeleteOpen(true)}
              onError={(message) => toast.error(message)}
            />
          ) : (
            <ReadOnlyOrder order={order} />
          )}
        </>
      )}

      <AlertDialog
        open={cutoff != null}
        onOpenChange={(open) => !open && setCutoff(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cutoffDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('cutoffDialog.body', {
                cutoffTime: cutoff?.cutoffTime ?? '',
                date: formatIsoDate(cutoff?.deliveryDate),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cutoffDialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setCutoff(null)
                sendOrder(true)
              }}
            >
              {t('cutoffDialog.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteDialog.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('deleteDialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                remove.mutate(undefined, {
                  onSuccess: () => {
                    toast.success(t('toast.deleted'))
                    navigate({ to: '/inventory/purchase-orders' })
                  },
                  onError: fail,
                })
              }
            >
              {t('deleteDialog.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
